/**
 * Painel lateral direito do DESKTOP largo (≥1440px): ocupa o espaço que sobraria vazio
 * ao lado da coluna das telas com o que interessa a quem bate ponto —
 *  - relógio ao vivo + data e saudação;
 *  - conexão e pendências de sincronização (mesma regra do "Sair"), com "Sincronizar agora";
 *  - calendário do mês com o dia de hoje.
 * Não existe no celular/tablet (nem é montado: nada de timer/leitura de fila lá).
 */
import { useAuth } from "@/contexts/AuthContext";
import { router } from "@/navigation";
import { SyncService } from "@/services/Sync";
import { MaterialCommunityIcons, MaterialIcons } from "@/ui/icons";
import { useMediaQuery } from "@/ui/useMediaQuery";
import { useEffect, useMemo, useState } from "react";
import s from "./PainelLateralDesktop.module.css";

const DIAS_SEMANA = ["D", "S", "T", "Q", "Q", "S", "S"];
const MESES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

function saudacao(h: number) {
  if (h < 12) return "Bom dia";
  if (h < 18) return "Boa tarde";
  return "Boa noite";
}

function primeiroNome(nome?: string) {
  const p = String(nome ?? "").trim().split(/\s+/)[0] ?? "";
  return p ? p.charAt(0).toUpperCase() + p.slice(1).toLowerCase() : "";
}

function useAgora() {
  const [agora, setAgora] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setAgora(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return agora;
}

function useConexao() {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
}

type Pendencias = Awaited<ReturnType<typeof SyncService.detalharPendenciasSync>>;

/**
 * Pendências de envio com a MESMA regra do "Sair" (SyncService.detalharPendenciasSync).
 * Antes o painel lia só a fila e dizia "Tudo enviado" enquanto o "Sair" seguia bloqueado
 * por batidas guardadas fora dela (mapa local / cópia em arquivo).
 */
function usePendencias() {
  const [pend, setPend] = useState<Pendencias | null>(null);
  const [tique, setTique] = useState(0);
  useEffect(() => {
    let vivo = true;
    const ler = () =>
      SyncService.detalharPendenciasSync()
        .then((p) => vivo && setPend(p))
        .catch(() => {});
    void ler();
    const id = setInterval(ler, 8000);
    // Batida nova, enviada ou confirmada: relê na hora (não espera o próximo ciclo).
    const parar = SyncService.observarPontosLocais(() => void ler());
    return () => {
      vivo = false;
      clearInterval(id);
      parar();
    };
  }, [tique]);
  return [pend, () => setTique((n) => n + 1)] as const;
}

function Relogio() {
  const agora = useAgora();
  const { userData } = useAuth();
  const hh = String(agora.getHours()).padStart(2, "0");
  const mm = String(agora.getMinutes()).padStart(2, "0");
  const ss = String(agora.getSeconds()).padStart(2, "0");
  const data = agora.toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long" });
  const nome = primeiroNome((userData as any)?.nome);
  return (
    <section className={`${s.cartao} ${s.relogio}`}>
      <span className={s.saudacao}>
        {saudacao(agora.getHours())}
        {nome ? `, ${nome}` : ""}
      </span>
      <div className={s.hora}>
        <span className={s.horaNumeros}>
          {hh}
          <span className={s.doisPontos}>:</span>
          {mm}
        </span>
        <span className={s.segundos}>{ss}</span>
      </div>
      <span className={s.data}>{data.charAt(0).toUpperCase() + data.slice(1)}</span>
      <button type="button" className={s.botaoPonto} onClick={() => router.push("/(tabs)/camera")}>
        <MaterialIcons name="photo-camera" size={18} color="#294956" />
        <span>Registrar ponto</span>
      </button>
    </section>
  );
}

const ICONE_GRUPO: Record<string, string> = {
  ponto: "fingerprint",
  assinatura: "draw",
  ronda: "radar",
  livro: "book-open-variant",
  outra: "cloud-upload-outline",
};

function StatusEnvio() {
  const online = useConexao();
  const [pend, reler] = usePendencias();
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState<string | null>(null);
  const itens = pend?.itens ?? [];
  const total = itens.length;

  const sincronizar = async () => {
    if (enviando) return;
    setEnviando(true);
    setResultado(null);
    try {
      // Sincronização COMPLETA (reconcilia batidas fora da fila, drena a fila e o resto),
      // a mesma do "puxar para atualizar" — não só o dreno da fila.
      await SyncService.syncAll({ manual: true });
    } catch {
      /* o que não subiu continua guardado; o agendador tenta de novo */
    }
    try {
      const depois = await SyncService.detalharPendenciasSync();
      setResultado(
        depois.itens.length === 0 && !depois.indeterminado
          ? "Sincronizado. Nada pendente neste aparelho."
          : `Ainda ${depois.itens.length === 1 ? "falta 1 item" : `faltam ${depois.itens.length} itens`} — tente de novo em instantes.`,
      );
    } catch {
      /* sem leitura agora */
    }
    setEnviando(false);
    reler();
  };

  const copiarDiagnostico = async () => {
    const texto = JSON.stringify(
      { quando: new Date().toISOString(), online, pendencias: pend },
      null,
      2,
    );
    try {
      await navigator.clipboard.writeText(texto);
      setResultado("Diagnóstico copiado. Cole na conversa com o suporte.");
    } catch {
      setResultado("Não deu para copiar automaticamente.");
    }
  };

  return (
    <section className={s.cartao}>
      <div className={s.cartaoTitulo}>
        <MaterialCommunityIcons name="cloud-sync-outline" size={18} color="#294956" />
        <span>Sincronização</span>
      </div>
      <div className={s.linhaStatus}>
        <span className={online ? `${s.ponto} ${s.pontoOnline}` : `${s.ponto} ${s.pontoOffline}`} />
        <span className={s.statusTexto}>{online ? "Conectado" : "Sem conexão — modo offline"}</span>
      </div>

      {pend == null ? null : total === 0 && !pend.indeterminado ? (
        <div className={s.filaOk}>
          <MaterialIcons name="check-circle" size={18} color="#10b981" />
          <span>Tudo enviado. Nada aguardando sincronização.</span>
        </div>
      ) : (
        <div className={s.filaPendente}>
          <div className={s.filaNumero}>{total || "?"}</div>
          <div className={s.filaTextos}>
            <span className={s.filaTitulo}>
              {total === 0
                ? "Não deu para conferir o que falta enviar"
                : total === 1
                  ? "item aguardando sincronização"
                  : "itens aguardando sincronização"}
            </span>
            <span className={s.filaSub}>
              {pend.motivo ?? "guardado neste aparelho — sair da conta fica bloqueado até enviar"}
            </span>
          </div>
          {total > 0 ? (
            <ul className={s.listaPendencias}>
              {itens.slice(0, 6).map((it, i) => (
                <li key={i} className={s.itemPendencia}>
                  <MaterialCommunityIcons name={ICONE_GRUPO[it.grupo] as any} size={16} color="#c2410c" />
                  <span className={s.itemTextos}>
                    <span className={s.itemTitulo}>{it.titulo}</span>
                    <span className={s.itemDetalhe}>{it.detalhe}</span>
                  </span>
                </li>
              ))}
              {itens.length > 6 ? <li className={s.itemMais}>+ {itens.length - 6} outros</li> : null}
            </ul>
          ) : null}
        </div>
      )}

      <button type="button" className={s.botaoEnviar} onClick={sincronizar} disabled={!online || enviando}>
        {enviando ? "Sincronizando…" : "Sincronizar agora"}
      </button>
      {resultado ? <span className={s.resultadoSync}>{resultado}</span> : null}
      {pend && (total > 0 || pend.indeterminado) ? (
        <button type="button" className={s.linkDiagnostico} onClick={copiarDiagnostico}>
          Copiar diagnóstico
        </button>
      ) : null}
    </section>
  );
}

function Calendario() {
  const agora = useAgora();
  const ano = agora.getFullYear();
  const mes = agora.getMonth();
  const hoje = agora.getDate();
  const dias = useMemo(() => {
    const primeiro = new Date(ano, mes, 1).getDay();
    const total = new Date(ano, mes + 1, 0).getDate();
    const celulas: (number | null)[] = Array.from({ length: primeiro }, () => null);
    for (let d = 1; d <= total; d++) celulas.push(d);
    while (celulas.length % 7) celulas.push(null);
    return celulas;
  }, [ano, mes]);
  return (
    <section className={s.cartao}>
      <div className={s.cartaoTitulo}>
        <MaterialIcons name="event" size={18} color="#294956" />
        <span>
          {MESES[mes]} {ano}
        </span>
      </div>
      <div className={s.calendario}>
        {DIAS_SEMANA.map((d, i) => (
          <span key={`c${i}`} className={s.calCabecalho}>
            {d}
          </span>
        ))}
        {dias.map((d, i) => (
          <span
            key={i}
            className={[s.calDia, d === hoje ? s.calHoje : "", i % 7 === 0 || i % 7 === 6 ? s.calFimSemana : ""].join(" ")}
          >
            {d ?? ""}
          </span>
        ))}
      </div>
    </section>
  );
}

export default function PainelLateralDesktop() {
  const largo = useMediaQuery("(min-width: 1440px)");
  if (!largo) return null;
  return (
    <div className={s.painel}>
      <div className={s.faixa} aria-hidden />
      <div className={s.pilha}>
        <Relogio />
        <StatusEnvio />
        <Calendario />
        <span className={s.rodape}>
          <MaterialCommunityIcons name="shield-check-outline" size={14} color="#8aa3ad" /> Funciona offline · seus registros ficam
          guardados até enviar
        </span>
      </div>
    </div>
  );
}
